import { useEffect, useState } from 'react'

const STORAGE_KEY = 'dm-countdown-deadline'
// 23h 43m 22s window
const WINDOW_MS = (23 * 3600 + 43 * 60 + 22) * 1000

function getDeadline(): number {
  if (typeof window === 'undefined') return Date.now() + WINDOW_MS
  const stored = window.localStorage.getItem(STORAGE_KEY)
  const now = Date.now()
  if (stored) {
    const parsed = parseInt(stored, 10)
    if (!Number.isNaN(parsed) && parsed > now) return parsed
  }
  const deadline = now + WINDOW_MS
  window.localStorage.setItem(STORAGE_KEY, String(deadline))
  return deadline
}

export interface TimeLeft {
  hours: number
  minutes: number
  seconds: number
}

function format(ms: number): TimeLeft {
  const clamped = Math.max(0, ms)
  const totalSeconds = Math.floor(clamped / 1000)
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  }
}

export function useCountdown(): TimeLeft {
  const [deadline, setDeadline] = useState<number>(() => getDeadline())
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
    format(deadline - Date.now()),
  )

  useEffect(() => {
    const tick = () => {
      const remaining = deadline - Date.now()
      if (remaining <= 0) {
        // restart a fresh 24h window
        const next = Date.now() + WINDOW_MS
        window.localStorage.setItem(STORAGE_KEY, String(next))
        setDeadline(next)
        setTimeLeft(format(WINDOW_MS))
        return
      }
      setTimeLeft(format(remaining))
    }
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [deadline])

  return timeLeft
}
